<template>
  <ContentWrap>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="节日" name="festival">
        <DictTable
          :loading="loading.festival"
          :list="festivalList"
          :columns="festivalCols"
          perm-prefix="market:festival"
          @add="openFestival()"
          @edit="openFestival"
          @delete="deleteFestival"
        />
      </el-tab-pane>
      <el-tab-pane label="行业" name="industry">
        <DictTable
          :loading="loading.industry"
          :list="industryList"
          :columns="industryCols"
          perm-prefix="market:industry"
          @add="openIndustry()"
          @edit="openIndustry"
          @delete="deleteIndustry"
        />
      </el-tab-pane>
      <el-tab-pane label="场景" name="scene">
        <DictTable
          :loading="loading.scene"
          :list="sceneList"
          :columns="sceneCols"
          perm-prefix="market:scene"
          @add="openScene()"
          @edit="openScene"
          @delete="deleteScene"
        />
      </el-tab-pane>
      <el-tab-pane label="板块类型" name="boardType">
        <DictTable
          :loading="loading.boardType"
          :list="boardTypeList"
          :columns="boardTypeCols"
          perm-prefix="market:board-type"
          @add="openBoardType()"
          @edit="openBoardType"
          @delete="deleteBoardType"
        />
      </el-tab-pane>
    </el-tabs>
  </ContentWrap>

  <Dialog v-model="dialogVisible" :title="dialogTitle" width="520px">
    <el-form ref="dialogFormRef" :model="dialogForm" :rules="dialogRules" label-width="96px">
      <template v-for="col in dialogFields" :key="col.prop">
        <el-form-item :label="col.label" :prop="col.prop">
          <el-input-number
            v-if="col.type === 'integer'"
            v-model="dialogForm[col.prop]"
            :min="col.min ?? 1"
            :precision="0"
            :step="1"
            class="!w-full"
            controls-position="right"
          />
          <el-input-number
            v-else-if="col.type === 'number'"
            v-model="dialogForm[col.prop]"
            :min="col.min ?? 0"
            class="!w-full"
            controls-position="right"
          />
          <el-date-picker
            v-else-if="col.type === 'festivalDates'"
            v-model="festivalDateRange"
            class="!w-full"
            end-placeholder="结束日期"
            start-placeholder="开始日期"
            type="daterange"
            value-format="YYYY-MM-DD"
          />
          <el-input v-else v-model="dialogForm[col.prop]" :placeholder="col.placeholder" />
        </el-form-item>
      </template>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="submitDialog">确定</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import type { FormInstance, FormRules } from 'element-plus'
import * as DictApi from '@/api/market/dict'
import DictTable from './DictTable.vue'

defineOptions({ name: 'MarketDict' })

type DialogField = {
  prop: string
  label: string
  type?: 'text' | 'number' | 'integer' | 'festivalDates'
  min?: number
  placeholder?: string
}

const message = useMessage()
const activeTab = ref('festival')
const loading = reactive({ festival: false, industry: false, scene: false, boardType: false })
const festivalList = ref<any[]>([])
const industryList = ref<any[]>([])
const sceneList = ref<any[]>([])
const boardTypeList = ref<any[]>([])

const festivalCols = [
  { prop: 'festKey', label: '节日Key', width: 100 },
  { prop: 'name', label: '名称', minWidth: 140 },
  { prop: 'festivalDates', label: '日期串', minWidth: 160 },
  { prop: 'sort', label: '排序', width: 80 }
]
const industryCols = [
  { prop: 'industryKey', label: '行业Key', width: 100 },
  { prop: 'parentKey', label: '父级', width: 80 },
  { prop: 'name', label: '名称', minWidth: 140 },
  { prop: 'sort', label: '排序', width: 80 }
]
const sceneCols = [
  { prop: 'sceneKey', label: '场景Key', width: 100 },
  { prop: 'name', label: '名称', minWidth: 140 },
  { prop: 'sort', label: '排序', width: 80 }
]
const boardTypeCols = [
  { prop: 'typeCode', label: '类型编码', width: 120 },
  { prop: 'name', label: '名称', minWidth: 120 },
  { prop: 'refHint', label: '关联说明', minWidth: 180 },
  { prop: 'sort', label: '排序', width: 80 }
]

const dialogVisible = ref(false)
const dialogTitle = ref('')
const dialogForm = ref<Record<string, any>>({})
const dialogFields = ref<DialogField[]>([])
const dialogFormRef = ref<FormInstance>()
const festivalDateRange = ref<[string, string] | undefined>()
const dialogRules = ref<FormRules>({})
let dialogMode = 'festival-create' as string

const parseFestivalDateRange = (value?: string): [string, string] | undefined => {
  if (!value) return undefined
  const parts = value.split(',').map((item) => item.trim()).filter(Boolean)
  if (parts.length < 2) return undefined
  const normalize = (raw: string) => {
    const date = new Date(raw.replace(/-/g, '/'))
    if (Number.isNaN(date.getTime())) return raw
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }
  return [normalize(parts[0]), normalize(parts[1])]
}

const formatFestivalDates = (range?: [string, string]) => {
  if (!range?.[0] || !range?.[1]) return ''
  const compact = (raw: string) => {
    const date = new Date(raw.replace(/-/g, '/'))
    if (Number.isNaN(date.getTime())) return raw
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
  }
  return `${compact(range[0])},${compact(range[1])}`
}

const loadAll = async () => {
  loading.festival = true
  loading.industry = true
  loading.scene = true
  loading.boardType = true
  try {
    festivalList.value = await DictApi.getMarketFestivalList()
    industryList.value = await DictApi.getMarketIndustryList()
    sceneList.value = await DictApi.getMarketSceneList()
    boardTypeList.value = await DictApi.getMarketBoardTypeList()
  } catch (e: any) {
    message.error(e?.message || '加载字典失败')
  } finally {
    loading.festival = false
    loading.industry = false
    loading.scene = false
    loading.boardType = false
  }
}

const openFestival = (row?: any) => {
  dialogMode = row ? 'festival-update' : 'festival-create'
  dialogTitle.value = row ? '编辑节日' : '新增节日'
  dialogFields.value = [
    { prop: 'festKey', label: '节日Key', type: 'integer', min: 1 },
    { prop: 'name', label: '名称', placeholder: '如：中秋节' },
    { prop: 'festivalDates', label: '节日日期', type: 'festivalDates' },
    { prop: 'sort', label: '排序', type: 'number', min: 0 }
  ]
  dialogRules.value = {
    festKey: [{ required: true, message: '请填写节日 Key（整数）', trigger: 'blur' }],
    name: [{ required: true, message: '请填写名称', trigger: 'blur' }]
  }
  dialogForm.value = row ? { ...row } : { festKey: undefined, name: '', sort: 0, status: 0 }
  festivalDateRange.value = parseFestivalDateRange(row?.festivalDates)
  dialogVisible.value = true
}

const openIndustry = (row?: any) => {
  dialogMode = row ? 'industry-update' : 'industry-create'
  dialogTitle.value = row ? '编辑行业' : '新增行业'
  dialogFields.value = [
    { prop: 'industryKey', label: '行业Key', type: 'integer', min: 1 },
    { prop: 'parentKey', label: '父级Key', type: 'number', min: 0 },
    { prop: 'name', label: '名称' },
    { prop: 'sort', label: '排序', type: 'number', min: 0 }
  ]
  dialogRules.value = {
    industryKey: [{ required: true, message: '请填写行业 Key', trigger: 'blur' }],
    name: [{ required: true, message: '请填写名称', trigger: 'blur' }]
  }
  dialogForm.value = row ? { ...row } : { industryKey: undefined, parentKey: 0, name: '', sort: 0, status: 0 }
  festivalDateRange.value = undefined
  dialogVisible.value = true
}

const openScene = (row?: any) => {
  dialogMode = row ? 'scene-update' : 'scene-create'
  dialogTitle.value = row ? '编辑场景' : '新增场景'
  dialogFields.value = [
    { prop: 'sceneKey', label: '场景Key', type: 'integer', min: 1 },
    { prop: 'name', label: '名称' },
    { prop: 'explainText', label: '说明' },
    { prop: 'sort', label: '排序', type: 'number', min: 0 }
  ]
  dialogRules.value = {
    sceneKey: [{ required: true, message: '请填写场景 Key', trigger: 'blur' }],
    name: [{ required: true, message: '请填写名称', trigger: 'blur' }]
  }
  dialogForm.value = row ? { ...row } : { sceneKey: undefined, name: '', sort: 0, status: 0 }
  festivalDateRange.value = undefined
  dialogVisible.value = true
}

const openBoardType = (row?: any) => {
  dialogMode = row ? 'boardType-update' : 'boardType-create'
  dialogTitle.value = row ? '编辑板块类型' : '新增板块类型'
  dialogFields.value = [
    { prop: 'typeCode', label: '类型编码', placeholder: '如 hot / festival' },
    { prop: 'name', label: '名称' },
    { prop: 'refHint', label: '关联说明' },
    { prop: 'sort', label: '排序', type: 'number', min: 0 }
  ]
  dialogRules.value = {
    typeCode: [{ required: true, message: '请填写类型编码', trigger: 'blur' }],
    name: [{ required: true, message: '请填写名称', trigger: 'blur' }]
  }
  dialogForm.value = row ? { ...row } : { typeCode: '', name: '', refHint: '', sort: 0, status: 0 }
  festivalDateRange.value = undefined
  dialogVisible.value = true
}

const submitDialog = async () => {
  await dialogFormRef.value?.validate()
  const data = { ...dialogForm.value }
  if (dialogMode.startsWith('festival')) {
    data.festivalDates = formatFestivalDates(festivalDateRange.value)
  }
  if (dialogMode.startsWith('festival')) {
    dialogMode.endsWith('create')
      ? await DictApi.createMarketFestival(data)
      : await DictApi.updateMarketFestival(data)
  } else if (dialogMode.startsWith('industry')) {
    dialogMode.endsWith('create')
      ? await DictApi.createMarketIndustry(data)
      : await DictApi.updateMarketIndustry(data)
  } else if (dialogMode.startsWith('boardType')) {
    dialogMode.endsWith('create')
      ? await DictApi.createMarketBoardType(data)
      : await DictApi.updateMarketBoardType(data)
  } else {
    dialogMode.endsWith('create')
      ? await DictApi.createMarketScene(data)
      : await DictApi.updateMarketScene(data)
  }
  message.success('已保存')
  dialogVisible.value = false
  await loadAll()
}

const deleteFestival = async (id: number) => {
  await message.delConfirm()
  await DictApi.deleteMarketFestival(id)
  await loadAll()
}
const deleteIndustry = async (id: number) => {
  await message.delConfirm()
  await DictApi.deleteMarketIndustry(id)
  await loadAll()
}
const deleteScene = async (id: number) => {
  await message.delConfirm()
  await DictApi.deleteMarketScene(id)
  await loadAll()
}
const deleteBoardType = async (id: number) => {
  await message.delConfirm()
  await DictApi.deleteMarketBoardType(id)
  await loadAll()
}

onMounted(loadAll)
</script>
